<script setup>
import { inject, onMounted } from 'vue'

import { storeToRefs } from 'pinia'
import { event } from 'vue-gtag'

import { useNotificationPermissionStore } from '@/stores/helpers/notificationPermission.js'
import { useNotificationDeviceStore } from '@/stores/retailer/notificationDevice.js'

import BaseButton from '@/components/BaseButton.vue'
import ModalContent from '@/components/ModalContent.vue'

const firebaseManager = inject('firebaseManager')
const notificationDeviceStore = useNotificationDeviceStore()
const notificationPermissionStore = useNotificationPermissionStore()

const { isNotificationPermission, isSystemNotificationApproved } = storeToRefs(notificationPermissionStore)

const props = defineProps({
  userType: {
    type: String,
    default: 'retailer'
  }
})

const lastShownDateKey = `${props.userType}LastShownNotificationPermissionDate`
const currentDate = () => new Date().toISOString().split('T')[0] // YYYY-MM-DD

const shouldShowModal = () => {
  if (Notification.permission === 'granted') {
    return false
  }

  const lastShownDate = localStorage.getItem(lastShownDateKey)

  return lastShownDate !== currentDate() && isSystemNotificationApproved.value === true
}

const saveLastShownDate = () => {
  localStorage.setItem(lastShownDateKey, currentDate())
}

const handleAllow = () => {
  event('notification_modal_approved')
  notificationPermissionStore.updateNotificationPermission(false)

  firebaseManager
    .getToken()
    .then(token => {
      event('notification_permission_obtained')
      notificationDeviceStore
        .createNotificationDevice({
          firebase_token: token
        })
        .then(() => {
          saveLastShownDate()
          firebaseManager.saveFirebaseTokenTimeToLocalStorage(props.userType)
        })
    })
    .catch(e => {
      if (Notification.permission === 'denied') {
        event('notification_permission_rejected')
      } else {
        saveLastShownDate()
        event('notification_permission_dismissed')
      }
    })
}

const handleLater = () => {
  event('notification_modal_postponed')
  notificationPermissionStore.updateNotificationPermission(false)
  saveLastShownDate()
}

onMounted(() => {
  if (!('Notification' in window)) {
    return
  }

  if (Notification.permission === 'granted') {
    if (firebaseManager.shouldSaveFirebaseToken(props.userType)) {
      firebaseManager.getToken().then(token => {
        notificationDeviceStore
          .createNotificationDevice({
            firebase_token: token
          })
          .then(() => {
            firebaseManager.saveFirebaseTokenTimeToLocalStorage(props.userType)
          })
      })
    }
    return
  }

  if (Notification.permission === 'denied') {
    return
  }

  notificationPermissionStore.updateNotificationPermission(shouldShowModal())

  if (isNotificationPermission.value) {
    event('notification_modal_shown')
  }
})
</script>

<template>
  <modal-content
    footerClass="border-top-0 justify-content-center"
    :isHeaderOn="false"
    :isShowing="isNotificationPermission"
  >
    <template v-slot:body>
      <div class="d-flex mt-2 px-3">
        <div class="notification-icon">
          <img src="/images/notification-icon.svg" />
        </div>
        <div class="ms-2">
          <div class="notification-permission-title fw-semibold text-lg">
            {{ $t('components.notification_permission_modal.title') }}
          </div>
          <div class="mt-2">
            <div class="notification-permission-description">
              <div>
                {{ $t('components.notification_permission_modal.description_header') }}
              </div>
              <ul class="ps-3 mt-1 mb-1">
                <li>
                  {{ $t('components.notification_permission_modal.description_list_1') }}
                </li>
                <li>
                  {{ $t('components.notification_permission_modal.description_list_2') }}
                </li>
                <li>
                  {{ $t('components.notification_permission_modal.description_list_3') }}
                </li>
              </ul>
              <div>
                {{ $t('components.notification_permission_modal.description_footer') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="px-2 pb-2 d-flex flex-column w-100">
        <base-button item-class="btn-primary mb-2" @click="handleAllow">
          {{ $t('components.notification_permission_modal.allow_button') }}
        </base-button>
        <base-button item-class="text-black-50 text-underline" @click="handleLater">
          {{ $t('components.notification_permission_modal.later_button') }}
        </base-button>
      </div>
    </template>
  </modal-content>
</template>

<style scoped>
.notification-icon {
  & img {
    background-color: #f6f6f6;
    border-radius: 4px;
  }
}
</style>
